import { Link } from 'react-router-dom';
import './SectionHome.css';

/** One launcher card: a page (or table) someone can open from a landing page. */
export interface LauncherCard {
  /** Unique within the page; used for the description's id. */
  key: string;
  label: string;
  route: string;
  /** Bootstrap icon class, e.g. `bi-printer`. */
  icon: string;
  description?: string;
}

const slug = (s: string) => s.replace(/\W+/g, '-').toLowerCase();

/**
 * One group of launcher cards under its heading: icon, bold name and a one-line description,
 * the whole card one link named exactly as its visible name. The look is SectionHome.css
 * (Setup, Jeff 2026-10-22: the plain list "looks very plain"); the Tables page uses the same
 * cards (Jeff 2026-10-25: its blue link list "looks really old school"), so there is one card
 * style. `ruled` draws a rule under the heading.
 */
export default function LauncherCardGroup({ idPrefix, title, cards, ruled = false }: {
  /** Prefix for the heading and description ids, unique on the page. */
  idPrefix: string;
  title: string;
  cards: LauncherCard[];
  ruled?: boolean;
}) {
  const headingId = `${idPrefix}-${slug(title || 'pages')}`;
  return (
    <section className="section-home__group" aria-labelledby={title ? headingId : undefined}>
      {title && (
        <h3 id={headingId} className={`section-home__heading${ruled ? ' section-home__heading--ruled' : ''}`}>
          {title}
        </h3>
      )}
      <ul className="section-home__grid">
        {cards.map((card) => {
          const descId = `${idPrefix}-desc-${slug(card.key)}`;
          return (
            <li key={card.key}>
              <Link
                to={card.route}
                className="section-home__card"
                aria-label={card.label}
                aria-describedby={card.description ? descId : undefined}
              >
                <span className="section-home__icon" aria-hidden="true">
                  <i className={`bi ${card.icon}`} />
                </span>
                <span className="section-home__text">
                  <span className="section-home__name">{card.label}</span>
                  {card.description && (
                    <span id={descId} className="section-home__desc">{card.description}</span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
